import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  FileText,
  Calendar,
  Target,
  CalendarCheck,
  UserCheck,
  ChevronDown,
  ChevronUp,
  Clock,
} from "lucide-react";
import { TreatmentPlan } from "@/lib/treatmentPlan/treatmentPlanData";
import { Appointment } from "@/lib/dashboard/appointmentsData";
import { cn } from "@/lib/utils";
import {
  addPatientConsultation,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  updateConsultationStatus,
  updateEventStatus,
  setPatientAIChatMessages,
  getPreConsultationByConsultationId,
  setVaccineDetails,
  updateConsultationTemplate,
  getPaymentAuditByConsultation,
  setPaymentAudit,
  setPatientTabSwitcher,
  setHasPreConsultTab,
  getPatientByPatientId,
  getPatientConsultationByConsultationId,
  getPatientConsultations,
  updateConsultationActions,
} from "@/dispatchers/patientDispatchers";
import {
  setPatientInfoTab,
  setisPatientSearched,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
  setRoutedFromScheduler,
  setConsultationLoadError,
} from "@/dispatchers/appDispatchers";
import {
  setHydratedTemplateSections,
  setIsTemplateSelected,
} from "@/dispatchers/templateDispatchers";
import {
  setLinkedTreatmentPlan,
  clearLinkedTreatmentPlan,
  updateTreatmentPlan,
  getPatientActiveTreatmentPlan,
  linkAppointmentConsultation,
  updateTreatmentPlanFollowUpStatus,
} from "@/dispatchers/treatmentPlanDispatchers";
import { addScheduleEvent } from "@/dispatchers/scheduleEventsDispatchers";
import { useNavigate } from "react-router-dom";
import { useTitanApp } from "@/hooks/useTitanApp";
import { v4 as uuidv4 } from "uuid";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import { setShowNHS111ReferralTab } from "@/dispatchers/pharmacyServiceDispatchers";

interface LinkTreatmentPlanDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activePlans: TreatmentPlan[];
  appointments?: Appointment[];
  onSelectPlan: (
    planId: string,
    linkType: "appointment" | "adhoc",
    appointmentId?: string,
  ) => void;
  onSkip: () => void;
  isDarkMode?: boolean;
  fromConsultataion?: boolean;
}

export const LinkTreatmentPlanDialog: React.FC<
  LinkTreatmentPlanDialogProps
> = ({
  open,
  onOpenChange,
  activePlans,
  appointments = [],
  onSelectPlan,
  onSkip,
  isDarkMode = false,
  fromConsultataion = false,
}) => {
  const { patientState, appState, treatmentPlanState } = useTitanApp();
  const { patientActiveTreatmentPlans } = treatmentPlanState || {};
  const { currentPharmacy, isAppLoading } = appState || {};
  const { darkMode, currentPharmacyId, companyId, features, currentUser } =
    appState || {};
  const { ifPharmacyDashboardEnabled } = features || {};
  const {
    selectedPatientDetails,
    selectedTemplate,
    hydratedTemplateSections,
    patientTranscription,
    addPatientConsultationLoader,
    patientAppointmentList,
    transcript,
    isPatientDetailsLoading,
    preConsultData,
    // ── current consultation ID (used for fromConsultataion adhoc flow) ──
    consultationId: currentConsultationId,
    consultationData,
  } = patientState || {};
  const navigate = useNavigate();

  // API returns treatmentPlanId/_id; TreatmentPlan type uses id — normalise
  const getPlanId = (plan: any): string =>
    plan.treatmentPlanId || plan._id || plan.id || "";

  // API returns status as integer; normalise to string
  const planStatusMap: Record<number, string> = {
    1: "Active",
    2: "Completed",
    3: "Paused",
    4: "Cancelled",
  };
  const getPlanStatus = (plan: any): string => {
    const s = plan.status;
    if (typeof s === "number") return planStatusMap[s] ?? "Active";
    return s || "Active";
  };

  const [selectedPlanId, setSelectedPlanId] = React.useState<string | null>(
    activePlans.length === 1 ? getPlanId(activePlans[0]) : null,
  );
  const [linkType, setLinkType] = React.useState<"appointment" | "adhoc">(
    "appointment",
  );
  const [selectedAppointmentId, setSelectedAppointmentId] = React.useState<
    string | null
  >(null);
  const [isAppointmentListOpen, setIsAppointmentListOpen] =
    React.useState(false);
  const [isCreatingAdhocConsultation, setIsCreatingAdhocConsultation] =
    React.useState(false);
  const [isLinkingAppointment, setIsLinkingAppointment] = React.useState(false);

  // Auto-select if only one plan
  React.useEffect(() => {
    if (activePlans.length === 1) {
      setSelectedPlanId(getPlanId(activePlans[0]));
    }
  }, [activePlans]);

  const handleContinue = () => {
    if (selectedPlanId) {
      onSelectPlan(
        selectedPlanId,
        linkType,
        linkType === "appointment"
          ? selectedAppointmentId || undefined
          : undefined,
      );
    } else {
      onSkip();
    }
    onOpenChange(false);
  };

  const startNewConsultation = async () => {
    setHasPreConsultTab(false);
    setPaymentAudit(null);
    setFreeTypeMode(false);
    setTranscript("");
    setPatientNotes("");
    setHydratedTemplateSections(null);
    setSignatureDetails(null);
    setSelectedPatientConsultation(null);
    setPatientAIChatMessages([]);
    const newConsultationId = uuidv4();
    setConsultationId(newConsultationId);
    setisHandleStartConsultation(true);
    setIsFinalSummary(true);
    setVaccineDetails(null);

    try {
      const consultationData = {
        consultationId: newConsultationId,
        patientId: selectedPatientDetails?.patientID,
        patientName:
          (selectedPatientDetails?.foreName || "") +
          " " +
          (selectedPatientDetails?.surName || ""),
        pharmacyID: currentPharmacyId,
        serviceID: "6880e77b98b936f8365ab340",
        serviceName: "General Service",
        consultationSummary: "",
        transcription: "",
        actions: [],
        companyID: companyId,
        consultationStatus: 2,
        createdByName: currentUser?.fullName || "",
        consultationButtonStatus: ConsultationButtonStatus.InProgress,
      };

      const result = await addPatientConsultation(consultationData);
      if (result === true) {
        setPatientInfoTab(1);
        setShowNHS111ReferralTab(false);
        navigate(
          `/pharmacy/patientconsultation/${currentPharmacyId}/${selectedPatientDetails?.patientID}/${newConsultationId}`,
        );
        return true;
      }

      return false;
    } catch (error) {
      setConsultationLoadError(true);
      console.error("Failed to save consultation:", error);
      return false;
    }
  };

  const handleSkip = async () => {
    clearLinkedTreatmentPlan();

    if (selectedPatientDetails?.patientID && currentPharmacyId) {
      const isStarted = await startNewConsultation();
      if (isStarted) {
        onOpenChange(false);
      }
      return;
    }

    onSkip();
    onOpenChange(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ADHOC HANDLER
  // When fromConsultataion=true: update the existing plan by appending replica
  // followups (startDate=now, consultationID=current), consultationAction=null,
  // link the plan, close — no navigation.
  //
  // When fromConsultataion=false: original flow — create a brand new consultation.
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCreateAdhocConsultation = async () => {
    if (!selectedPlanId) {
      console.error("❌ No plan selected for ad-hoc");
      return;
    }

    setIsCreatingAdhocConsultation(true);

    try {
      const selectedPlan = activePlans.find(
        (p) => getPlanId(p) === selectedPlanId,
      );

      if (!selectedPlan) {
        console.error("❌ Selected treatment plan not found");
        setIsCreatingAdhocConsultation(false);
        return;
      }

      // ── fromConsultataion=true: update plan with replica followups ───────────
      if (fromConsultataion) {
        // Format matches the rest of the codebase: yyyy-MM-dd'T'HH:mm:ss'Z' (no ms)
        const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
        const existingFollowUps: any[] =
          (selectedPlan as any).followUpAppointments || [];

        // Find only the single upcoming followup to replicate:
        // - exclude any already linked to this consultation (prevents double-replicas)
        // - exclude cancelled (4) and completed (5)
        // - sort ascending by startDate, take the first (most upcoming)
        const todayMs = Date.now();
        const upcomingFollowUp = existingFollowUps
          .filter(
            (apt: any) =>
              apt.consultationID !== currentConsultationId &&
              apt.consultationId !== currentConsultationId &&
              apt.eventStatus !== 4 && // not Cancelled
              apt.eventStatus !== 5 && // not Completed
              new Date(apt.startDate).getTime() > todayMs, // future only
          )
          .sort(
            (a: any, b: any) =>
              new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
          )[0]; // nearest future date wins (e.g. 16 Apr beats 21 Apr)

        // Build replica — new id, startDate=now, linked to current consultation.
        // If no upcoming followup exists, a minimal mock object is created so
        // the adhoc flow can always proceed regardless of followup state.
        const replicaBase = upcomingFollowUp
          ? { ...upcomingFollowUp }
          : {
              // mock — no real followup available
              patientID: selectedPatientDetails?.patientID || "",
              patientId: selectedPatientDetails?.patientID || "",
              duration: 0,
              durationUnit: "m",
              notes: "",
              resourceId: currentUser.emailAddress,
              email: "",
              mobile: "",
              confirmationMethod: "",
              roomId: "",
            };
        const replicaFollowUpTrueId = uuidv4();
        const replicaFollowUps = [
          {
            ...replicaBase,
            id: replicaFollowUpTrueId,
            startDate: now,
            endDate: now,
            serviceID:
              upcomingFollowUp?.serviceID ||
              upcomingFollowUp?.serviceId ||
              "6880e77b98b936f8365ab340",
            scheduleEventType: 1,
            consultationID: currentConsultationId || "",
            eventStatus: 2, // Pending
            vaccinesAdminister: [],
            tasksToComplete: [],
          },
        ];

        // Register the replica as a schedule event
        const replica = replicaFollowUps[0];
        const scheduleEventTrue = {
          id: replicaFollowUpTrueId,
          consultationID: replica.consultationID,
          name: replica.notes || "",
          patientID:
            replica.patientID ||
            replica.patientId ||
            selectedPatientDetails?.patientID ||
            "",
          startDate: replica.startDate,
          endDate: replica.endDate,
          duration:
            typeof replica.duration === "string"
              ? parseInt(replica.duration, 10) || 0
              : replica.duration || 0,
          durationUnit: "m",
          cls: "",
          recurrenceRule: "",
          exceptionDates: [] as string[],
          resourceId: replica.resourceId || "",
          allDay: false,
          scheduleEventType: 1,
          serviceID: replica.serviceID || replica.serviceId || "",
          email: replica.email || "",
          mobile: replica.mobile || "",
          confirmationMethod: replica.confirmationMethod || "",
          RoomId: replica.roomId || "",
          recordedAt: new Date().toLocaleString(),
          tasksToComplete: [],
          vaccinesAdminister: [],
          IsNotify: true,
        };

        try {
          await addScheduleEvent(scheduleEventTrue, currentPharmacyId);
        } catch (scheduleErr) {
          console.warn(
            "addScheduleEvent (true case) failed, continuing with plan update:",
            scheduleErr,
          );
        }
        const updatedPlan = {
          ...(selectedPlan as any),
          treatmentPlanId: getPlanId(selectedPlan),
          patientId:
            (selectedPlan as any).patientId ||
            selectedPatientDetails?.patientID ||
            "",
          followUpAppointments: [...existingFollowUps, ...replicaFollowUps], // existingFollowUps preserved; only non-linked ones replicated
          updatedAt: now,
        };

        const requestBody = {
          TreatmentPlan: updatedPlan,
          // consultationAction is null for the fromConsultataion adhoc flow
          consultationAction: null,
        };

        await updateTreatmentPlan(requestBody);
        const eventData = {
          id: replica.id,
          eventStatus: 3,
        };
        await updateEventStatus(eventData);
        await updateTreatmentPlanFollowUpStatus({
          PatientID: selectedPatientDetails?.patientID || "",
          TreatmentPlanID: selectedPlanId || "",
          FollowUpAppointment: replica,
        });
        // Refresh active treatment plans so the UI reflects the update
        await getPatientActiveTreatmentPlan(
          (selectedPlan as any).patientId ||
            selectedPatientDetails?.patientID ||
            "",
        );

        // Link the plan (shows border / linked state)
        setLinkedTreatmentPlan(selectedPlanId, null);

        onOpenChange(false);
        return;
      }

      // ── fromConsultataion=false: replica of upcoming followup onto new consultation ──
      const newConsultationId = uuidv4();
      const nowFalse = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
      const existingFollowUpsFalse: any[] =
        (selectedPlan as any).followUpAppointments || [];

      // Find the single nearest upcoming followup to replicate
      const todayMsFalse = Date.now();
      const upcomingFollowUpFalse = existingFollowUpsFalse
        .filter(
          (apt: any) =>
            apt.eventStatus !== 4 && // not Cancelled
            apt.eventStatus !== 5 && // not Completed
            new Date(apt.startDate).getTime() > todayMsFalse, // future only
        )
        .sort(
          (a: any, b: any) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
        )[0]; // nearest future date wins

      // Build the replica — new id, startDate=now, linked to the new consultation.
      // If no upcoming followup exists, a minimal mock object is created so
      // the adhoc flow can always proceed regardless of followup state.
      const replicaBaseF = upcomingFollowUpFalse
        ? { ...upcomingFollowUpFalse }
        : {
            // mock — no real followup available
            patientID: selectedPatientDetails?.patientID || "",
            patientId: selectedPatientDetails?.patientID || "",
            duration: 0,
            durationUnit: "m",
            notes: "",
            resourceId: currentUser.emailAddress,
            email: "",
            mobile: "",
            confirmationMethod: "",
            roomId: "",
          };
      const replicaFollowUpFalseId = uuidv4();
      const replicaFollowUpFalse = {
        ...replicaBaseF,
        id: replicaFollowUpFalseId,
        startDate: nowFalse,
        endDate: nowFalse,
        serviceID:
          upcomingFollowUpFalse?.serviceID ||
          upcomingFollowUpFalse?.serviceId ||
          "6880e77b98b936f8365ab340",
        scheduleEventType: 1,
        consultationID: newConsultationId,
        eventStatus: 2, // Pending
        vaccinesAdminister: [],
        tasksToComplete: [],
      };

      // Register the replica as a schedule event (mirrors TreatmentPlanModal)
      if (replicaFollowUpFalse) {
        const scheduleEvent = {
          id: replicaFollowUpFalseId,
          consultationID: replicaFollowUpFalse.consultationID,
          name: replicaFollowUpFalse.notes || "",
          patientID:
            replicaFollowUpFalse.patientID ||
            replicaFollowUpFalse.patientId ||
            selectedPatientDetails?.patientID ||
            "",
          startDate: replicaFollowUpFalse.startDate,
          endDate: replicaFollowUpFalse.endDate,
          duration:
            typeof replicaFollowUpFalse.duration === "string"
              ? parseInt(replicaFollowUpFalse.duration, 10) || 0
              : replicaFollowUpFalse.duration || 0,
          durationUnit: "m",
          cls: "",
          recurrenceRule: "",
          exceptionDates: [] as string[],
          resourceId: replicaFollowUpFalse.resourceId || "",
          allDay: false,
          scheduleEventType: 1,
          serviceID:
            replicaFollowUpFalse.serviceID ||
            replicaFollowUpFalse.serviceId ||
            "",
          email: replicaFollowUpFalse.email || "",
          mobile: replicaFollowUpFalse.mobile || "",
          confirmationMethod: replicaFollowUpFalse.confirmationMethod || "",
          RoomId: replicaFollowUpFalse.roomId || "",
          recordedAt: new Date().toLocaleString(),
          tasksToComplete: [],
          vaccinesAdminister: [],
          IsNotify: true,
        };

        try {
          await addScheduleEvent(scheduleEvent, currentPharmacyId);
        } catch (scheduleErr) {
          console.warn(
            "addScheduleEvent failed, continuing with plan update:",
            scheduleErr,
          );
        }
      }

      // Update the treatment plan — preserve existing followups, append replica
      const updatedPlanFalse = {
        ...(selectedPlan as any),
        treatmentPlanId: getPlanId(selectedPlan),
        patientId:
          (selectedPlan as any).patientId ||
          selectedPatientDetails?.patientID ||
          "",
        followUpAppointments: [...existingFollowUpsFalse, replicaFollowUpFalse],
        updatedAt: nowFalse,
      };

      await updateTreatmentPlan({
        TreatmentPlan: updatedPlanFalse,
        consultationAction: null,
      });
      const data = {
        consultationId: newConsultationId,
        actionStatus: 2,
      };
      await updateConsultationStatus(data);
      // Create the new consultation record
      // await addPatientConsultation({
      //   consultationId: newConsultationId,
      //   patientId: selectedPatientDetails?.patientID,
      //   patientName:
      //     (selectedPatientDetails?.foreName || "") +
      //     " " +
      //     (selectedPatientDetails?.surName || ""),
      //   pharmacyID: currentPharmacyId,
      //   serviceID: "686fd89ee7f84e759207bfe4",
      //   serviceName: "General Service",
      //   consultationSummary: "",
      //   transcription: "",
      //   actions: [],
      //   companyID: companyId,
      //   consultationStatus: 2,
      //   createdByName: currentUser?.fullName || "",
      //   consultationButtonStatus: ConsultationButtonStatus.InProgress,
      // });
      const eventData = {
        id: replicaFollowUpFalse.id,
        eventStatus: 3,
      };
      await updateEventStatus(eventData);
      await updateTreatmentPlanFollowUpStatus({
        PatientID: selectedPatientDetails?.patientID || "",
        TreatmentPlanID: selectedPlanId || "",
        FollowUpAppointment: replicaFollowUpFalse,
      });
      // Refresh active treatment plans
      await getPatientActiveTreatmentPlan(
        (selectedPlan as any).patientId ||
          selectedPatientDetails?.patientID ||
          "",
      );

      // Reset Redux consultation state so the component re-initialises
      // with the new consultation instead of staying on the old one.
      // setConsultationId is the key signal — the page reads from Redux, not the URL.
      setSelectedPatientConsultation(null);
      setPatientAIChatMessages([]);
      setConsultationId(newConsultationId);
      setisHandleStartConsultation(true);
      setIsFinalSummary(true);
      setPatientInfoTab(1);
      setShowNHS111ReferralTab(false);
      setIsTemplateSelected(true);

      // Link the plan and navigate to the new consultation
      setLinkedTreatmentPlan(selectedPlanId, null);

      navigate(
        `/pharmacy/patientconsultation/${currentPharmacyId}/${selectedPatientDetails?.patientID}/${newConsultationId}`,
      );

      onOpenChange(false);
    } catch (error) {
      setConsultationLoadError(true);
      console.error("❌ Failed to create ad-hoc consultation:", error);
    } finally {
      setIsCreatingAdhocConsultation(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const mapEventStatus = (eventStatus: number): Appointment["status"] => {
    switch (eventStatus) {
      case 1:
        return "Confirmed";
      case 2:
        return "Pending";
      case 3:
        return "Confirmed";
      case 4:
        return "Cancelled";
      case 5:
        return "Completed";
      default:
        return "Pending";
    }
  };

  const getAllAppointments = (plan: TreatmentPlan): Appointment[] => {
    if (!plan.followUpAppointments || plan.followUpAppointments.length === 0) {
      return [];
    }

    return (plan.followUpAppointments as any[])
      .filter((appt: any) => appt.eventStatus !== 1 && appt.eventStatus !== 3)
      .map((appt): Appointment => {
        const raw = typeof appt === "object" && appt !== null ? appt : null;
        if (!raw) return null as any;

        const startDate = raw.startDate ? new Date(raw.startDate) : null;
        const date = startDate
          ? startDate.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "";
        const time = startDate
          ? startDate.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            })
          : "";

        return {
          id: raw.id || raw._id || "",
          date,
          time,
          type: raw.serviceType || raw.name || raw.serviceName || "Appointment",
          status: mapEventStatus(raw.eventStatus),
          practitionerName: raw.resourceId || raw.pharmacist || "",
          patientConsultaitonId: raw.consultationID || raw.consultationId || "",
          patientID: raw.patientID || raw.patientId || "",
          eventStatus: raw.eventStatus,
          scheduleEventType: raw.scheduleEventType || raw.eventType || "",
          resourceId: raw.resourceId || currentUser?.emailAddress || "",
        };
      })
      .filter((apt): apt is Appointment => apt !== null)
      .filter((apt) => apt.status !== "Cancelled" && apt.status !== "Completed")
      .sort((a, b) => {
        const dateA = new Date(a.date.split("/").reverse().join("-"));
        const dateB = new Date(b.date.split("/").reverse().join("-"));
        return dateA.getTime() - dateB.getTime();
      });
  };

  const selectedPlan = activePlans.find((p) => getPlanId(p) === selectedPlanId);
  const availableAppointments = selectedPlan
    ? getAllAppointments(selectedPlan)
    : [];

  // Adhoc is available for any selected plan (no followups → mock object created)
  const canUseAdhoc = !!selectedPlan;

  const nextAppointment = availableAppointments[0] || null;

  React.useEffect(() => {
    if (!selectedPlanId) {
      setSelectedAppointmentId(null);
      return;
    }

    if (availableAppointments.length === 0) {
      setSelectedAppointmentId(null);
      return;
    }

    setSelectedAppointmentId((prevSelectedId) => {
      if (
        prevSelectedId &&
        availableAppointments.some(
          (apt) => apt.id.toString() === prevSelectedId,
        )
      ) {
        return prevSelectedId;
      }

      return availableAppointments[0].id.toString();
    });
  }, [selectedPlanId, availableAppointments]);

  React.useEffect(() => {
    if (selectedPlanId && !canUseAdhoc && linkType === "adhoc") {
      setLinkType("appointment");
    }
  }, [selectedPlanId, canUseAdhoc, linkType]);

  const selectedAppointment = availableAppointments.find(
    (apt) => apt.id.toString() === selectedAppointmentId,
  );

  const linktoSelectedAppointment = async () => {
    setIsLinkingAppointment(true);
    try {
      if (fromConsultataion) {
        const now = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

        const mockScheduleEvent = {
          id: uuidv4(),
          consultationID: currentConsultationId || "",
          name: "",
          patientID: selectedPatientDetails?.patientID || "",
          startDate: now,
          endDate: now,
          duration: 0,
          durationUnit: "m",
          cls: "",
          recurrenceRule: "",
          exceptionDates: [] as string[],
          resourceId: currentUser?.emailAddress || "",
          allDay: false,
          scheduleEventType: 1,
          serviceID: "6880e77b98b936f8365ab340",
          email: "",
          mobile: "",
          confirmationMethod: "",
          RoomId: "",
          recordedAt: new Date().toLocaleString(),
          tasksToComplete: [],
          vaccinesAdminister: [],
          eventStatus: 3,
          IsNotify: true,
        };

        const followUpAppointment = selectedAppointment
          ? {
              ...((selectedPlan as any)?.followUpAppointments?.find(
                (apt: any) =>
                  apt.id === selectedAppointment.id ||
                  apt._id === selectedAppointment.id,
              ) || {}),
              consultationID: currentConsultationId || "",
            }
          : mockScheduleEvent;

        if (!selectedAppointment) {
          try {
            await addScheduleEvent(mockScheduleEvent, currentPharmacyId);
          } catch (err) {
            console.warn(
              "addScheduleEvent (mock link appointment) failed:",
              err,
            );
          }
        }
        const eventData = {
          id: followUpAppointment.id,
          eventStatus: 3,
        };
        await updateEventStatus(eventData);
        try {
          await updateTreatmentPlanFollowUpStatus({
            PatientID: selectedPatientDetails?.patientID || "",
            TreatmentPlanID: selectedPlanId || "",
            FollowUpAppointment: followUpAppointment,
          });
        } catch (err) {
          console.warn("updateTreatmentPlanFollowUpStatus failed:", err);
        }

        try {
          await linkAppointmentConsultation({
            PatientID: selectedPatientDetails?.patientID || "",
            TreatmentPlanID: selectedPlanId || "",
            FollowUpAppointment: followUpAppointment,
          });
        } catch (err) {
          console.warn("linkAppointmentConsultation failed:", err);
        }
        await getPatientActiveTreatmentPlan(
          (selectedPlan as any).patientId ||
            selectedPatientDetails?.patientID ||
            "",
        );
        setLinkedTreatmentPlan(selectedPlanId, selectedAppointmentId || null);
        onOpenChange(false);
        return;
      }

      // ── fromConsultataion=false, no followups: create mock, addSchedule, link, navigate ──
      if (availableAppointments.length === 0) {
        const nowMock = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
        const mockConsultationId = uuidv4();
        const mockFollowUpId = uuidv4();

        const mockFollowUp = {
          id: mockFollowUpId,
          consultationID: mockConsultationId,
          name: "",
          patientID: selectedPatientDetails?.patientID || "",
          startDate: nowMock,
          endDate: nowMock,
          duration: 0,
          durationUnit: "m",
          cls: "",
          recurrenceRule: "",
          exceptionDates: [] as string[],
          resourceId: currentUser?.emailAddress || "",
          allDay: false,
          scheduleEventType: 1,
          serviceID: "6880e77b98b936f8365ab340",
          email: "",
          mobile: "",
          confirmationMethod: "",
          RoomId: "",
          recordedAt: new Date().toLocaleString(),
          tasksToComplete: [],
          vaccinesAdminister: [],
          eventStatus: 3,
          IsNotify: true,
        };

        try {
          await addScheduleEvent(mockFollowUp, currentPharmacyId);
        } catch (err) {
          console.warn(
            "addScheduleEvent (no-followup false case) failed:",
            err,
          );
        }

        try {
          await linkAppointmentConsultation({
            PatientID: selectedPatientDetails?.patientID || "",
            TreatmentPlanID: selectedPlanId || "",
            FollowUpAppointment: mockFollowUp,
          });
        } catch (err) {
          console.warn(
            "linkAppointmentConsultation (no-followup false case) failed:",
            err,
          );
        }

        await updateTreatmentPlanFollowUpStatus({
          PatientID: selectedPatientDetails?.patientID || "",
          TreatmentPlanID: selectedPlanId || "",
          FollowUpAppointment: mockFollowUp,
        });
        const data = {
          consultationId: mockConsultationId,
          actionStatus: 2,
        };
        await updateConsultationStatus(data);
        try {
          await updateEventStatus({ id: mockFollowUpId, eventStatus: 3 });
        } catch (err) {
          console.warn(
            "updateEventStatus (no-followup false case) failed:",
            err,
          );
        }

        // Reset Redux so the consultation component re-initialises
        setSelectedPatientConsultation(null);
        setPatientAIChatMessages([]);
        setConsultationId(mockConsultationId);
        setisHandleStartConsultation(true);
        setIsFinalSummary(true);
        setPatientInfoTab(1);
        setShowNHS111ReferralTab(false);
        setIsTemplateSelected(true);

        setLinkedTreatmentPlan(selectedPlanId, mockFollowUpId);
        navigate(
          `/pharmacy/patientconsultation/${currentPharmacyId}/${selectedPatientDetails?.patientID}/${mockConsultationId}`,
        );
        onOpenChange(false);
        return;
      }

      // ── fromConsultataion=false: original flow with navigation ───────────────
      const pid = selectedPlan?.patientId;
      const consultationID = selectedAppointment?.patientConsultaitonId;
      onOpenChange(false);
      setPatientAIChatMessages([]);
      await getPatientConsultationByConsultationId(consultationID || "");
      if (pid && currentPharmacyId && consultationID) {
        setHasPreConsultTab(false);
        setConsultationId(consultationID);
        await getPatientByPatientId(pid);
        setCurrentGlobalTabValue(ifPharmacyDashboardEnabled ? 4 : 3);
        setCurrentGlobalTabText("Patients");
        setisPatientSearched(true);
        setPatientInfoTab(1);
        setShowNHS111ReferralTab(false);
        setIsTemplateSelected(true);
        await getPatientConsultations(pid);
        const consultationRes =
          await getPatientConsultationByConsultationId(consultationID);
        setPatientTabSwitcher(consultationRes?.preConsultationTemplate ? 1 : 0);
        await getPaymentAuditByConsultation({ consultationID: consultationID });
        if (consultationRes?.preConsultationTemplate) {
          const preConsultDataRes = await getPreConsultationByConsultationId({
            consultationId: consultationRes?.consultationId,
          });
          setPreConsultationTemplate(
            preConsultDataRes?.preConsultationTemplate,
          );
          if (preConsultDataRes?.preConsultationTemplate) {
            let preConsultTranscription = "";
            preConsultDataRes.preConsultationTemplate.templateSections?.forEach(
              (section) => {
                section?.userControls?.forEach((control) => {
                  if (control.label && control.value) {
                    preConsultTranscription += ` ${control.label} ${control.value} `;
                  }
                });
              },
            );
            setPreConsultationTranscription(preConsultTranscription.trim());
          } else {
            setPreConsultationTranscription("");
          }
        } else {
          setPreConsultationTemplate(null);
          setPreConsultationTranscription("");
        }

        setLinkedTreatmentPlan(selectedPlanId, selectedAppointmentId);
        const data = {
          consultationId: consultationID,
          actionStatus: 2,
        };
        await updateConsultationStatus(data);
        const eventData = {
          id: selectedAppointment?.id,
          eventStatus: 3,
        };
        await updateEventStatus(eventData);
        try {
          await updateTreatmentPlanFollowUpStatus({
            PatientID: pid,
            TreatmentPlanID: selectedPlanId || "",
            FollowUpAppointment: {
              ...selectedAppointment,
              consultationID,
            },
          });
        } catch (err) {
          console.warn("updateTreatmentPlanFollowUpStatus failed:", err);
        }

        navigate(
          `/pharmacy/patientconsultation/${currentPharmacyId}/${pid}/${consultationID}`,
        );
      }
    } finally {
      setIsLinkingAppointment(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "max-w-2xl z-[3000] flex flex-col max-h-[85vh] overflow-hidden",
          isDarkMode
            ? "bg-gray-900 border-gray-700 text-gray-200"
            : "bg-white border-gray-200",
        )}
      >
        <DialogHeader>
          <DialogTitle
            className={cn(
              "flex items-center gap-2",
              isDarkMode ? "text-gray-100" : "text-gray-900",
            )}
          >
            <FileText
              className={cn(
                "w-5 h-5",
                isDarkMode ? "text-gray-400" : "text-gray-500",
              )}
            />
            Link Consultation to Treatment Plan
          </DialogTitle>
          <DialogDescription
            className={isDarkMode ? "text-gray-400" : "text-gray-600"}
          >
            This patient has {activePlans.length} active treatment plan
            {activePlans.length !== 1 ? "s" : ""}. Select a plan to link this
            consultation and choose how to record it.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 py-4 pr-1 space-y-4 overflow-y-auto">
          {/* Treatment Plan Selection */}
          <div>
            <Label
              className={cn(
                "text-sm font-semibold mb-2 block",
                isDarkMode ? "text-gray-300" : "text-gray-700",
              )}
            >
              Select Treatment Plan
            </Label>
            <ScrollArea className="h-[240px]">
              <div className="pr-4 space-y-3">
                {activePlans.map((plan) => (
                  <div
                    key={getPlanId(plan)}
                    onClick={() => setSelectedPlanId(getPlanId(plan))}
                    className={cn(
                      "p-4 rounded-lg border-2 cursor-pointer transition-all duration-200",
                      selectedPlanId === getPlanId(plan)
                        ? isDarkMode
                          ? "border-gray-400 bg-gray-800"
                          : "border-gray-900 bg-gray-50"
                        : isDarkMode
                          ? "border-gray-700 bg-gray-800 hover:border-gray-600"
                          : "border-gray-200 bg-white hover:border-gray-300",
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 space-y-2">
                        <div className="flex items-center gap-2">
                          <h3
                            className={cn(
                              "font-semibold",
                              isDarkMode ? "text-white" : "text-gray-900",
                            )}
                          >
                            {plan.name}
                          </h3>
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs",
                              getPlanStatus(plan).toLowerCase() === "active"
                                ? isDarkMode
                                  ? "bg-gray-700 text-gray-300 border-gray-600"
                                  : "bg-gray-100 text-gray-700 border-gray-300"
                                : "",
                            )}
                          >
                            {getPlanStatus(plan)}
                          </Badge>
                        </div>

                        {plan.description && (
                          <p
                            className={cn(
                              "text-sm",
                              isDarkMode ? "text-gray-400" : "text-gray-600",
                            )}
                          >
                            {plan.description}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-4 text-xs">
                          <div className="flex items-center gap-1.5">
                            <Calendar
                              className={cn(
                                "w-3.5 h-3.5",
                                isDarkMode ? "text-gray-500" : "text-gray-400",
                              )}
                            />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              Started: {formatDate(plan.startDate)}
                            </span>
                          </div>

                          {plan.endDate && (
                            <div className="flex items-center gap-1.5">
                              <Calendar
                                className={cn(
                                  "w-3.5 h-3.5",
                                  isDarkMode
                                    ? "text-gray-500"
                                    : "text-gray-400",
                                )}
                              />
                              <span
                                className={
                                  isDarkMode ? "text-gray-400" : "text-gray-600"
                                }
                              >
                                Ends: {formatDate(plan.endDate)}
                              </span>
                            </div>
                          )}

                          <div className="flex items-center gap-1.5">
                            <Target
                              className={cn(
                                "w-3.5 h-3.5",
                                isDarkMode ? "text-gray-500" : "text-gray-400",
                              )}
                            />
                            <span
                              className={
                                isDarkMode ? "text-gray-400" : "text-gray-600"
                              }
                            >
                              {plan.goals?.length || 0} goal
                              {plan.goals?.length !== 1 ? "s" : ""}
                            </span>
                          </div>
                        </div>
                      </div>

                      {selectedPlanId === getPlanId(plan) && (
                        <div className="flex-shrink-0">
                          <div className="flex items-center justify-center w-5 h-5 bg-gray-900 rounded-full">
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7"></path>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>

          {/* Consultation Type Selection */}
          {selectedPlanId && (
            <div
              className={cn(
                "p-4 rounded-lg border",
                isDarkMode
                  ? "bg-gray-800 border-gray-700"
                  : "bg-gray-50 border-gray-200",
              )}
            >
              <Label
                className={cn(
                  "text-sm font-semibold mb-3 block",
                  isDarkMode ? "text-gray-300" : "text-gray-700",
                )}
              >
                How would you like to record this consultation?
              </Label>

              <RadioGroup
                value={linkType}
                onValueChange={(value) =>
                  setLinkType(value as "appointment" | "adhoc")
                }
              >
                <div className="space-y-3">
                  {/* Link to Appointment Option */}
                  {availableAppointments.length > 0 ? (
                    <div
                      className={cn(
                        "rounded-lg border-2 transition-colors",
                        linkType === "appointment"
                          ? isDarkMode
                            ? "border-gray-400 bg-gray-800"
                            : "border-gray-900 bg-gray-50"
                          : isDarkMode
                            ? "border-gray-700 hover:border-gray-600"
                            : "border-gray-200 hover:border-gray-300",
                      )}
                    >
                      <div
                        className="flex items-start p-3 space-x-3 cursor-pointer"
                        onClick={() => setLinkType("appointment")}
                      >
                        <RadioGroupItem
                          value="appointment"
                          id="appointment"
                          className="mt-0.5"
                        />
                        <div className="flex-1 space-y-1">
                          <Label
                            htmlFor="appointment"
                            className={cn(
                              "cursor-pointer font-medium flex items-center gap-2",
                              isDarkMode ? "text-gray-200" : "text-gray-900",
                            )}
                          >
                            <CalendarCheck
                              className={cn(
                                "w-4 h-4",
                                isDarkMode ? "text-gray-400" : "text-gray-500",
                              )}
                            />
                            Link to Scheduled Appointment
                          </Label>
                          {selectedAppointment && (
                            <div
                              className={cn(
                                "text-sm pl-6",
                                isDarkMode ? "text-gray-400" : "text-gray-600",
                              )}
                            >
                              <div className="flex items-center gap-2 mt-1">
                                <Badge
                                  variant="outline"
                                  className={cn(
                                    "text-xs",
                                    isDarkMode
                                      ? "bg-gray-700 text-gray-300 border-gray-600"
                                      : "bg-gray-100 text-gray-700 border-gray-300",
                                  )}
                                >
                                  {selectedAppointment.date}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {selectedAppointment.time}
                                </Badge>
                                <span className="text-xs">
                                  - {selectedAppointment.type}
                                </span>
                              </div>
                              <p className="mt-1 text-xs opacity-80">
                                This consultation will be recorded against the
                                selected appointment
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Expandable Appointment List */}
                      {availableAppointments.length > 1 &&
                        linkType === "appointment" && (
                          <Collapsible
                            open={isAppointmentListOpen}
                            onOpenChange={setIsAppointmentListOpen}
                          >
                            <div
                              className={cn(
                                "border-t px-3 py-2",
                                isDarkMode
                                  ? "border-gray-700"
                                  : "border-gray-200",
                              )}
                            >
                              <CollapsibleTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={cn(
                                    "w-full justify-between text-xs font-medium",
                                    isDarkMode
                                      ? "text-gray-300 hover:text-gray-100"
                                      : "text-gray-700 hover:text-gray-900",
                                  )}
                                >
                                  <span className="flex items-center gap-2">
                                    <Clock className="w-3.5 h-3.5" />
                                    {isAppointmentListOpen
                                      ? "Hide"
                                      : "Show"}{" "}
                                    all {availableAppointments.length}{" "}
                                    appointments
                                  </span>
                                  {isAppointmentListOpen ? (
                                    <ChevronUp className="w-4 h-4" />
                                  ) : (
                                    <ChevronDown className="w-4 h-4" />
                                  )}
                                </Button>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="mt-2">
                                <ScrollArea className="h-[200px]">
                                  <div className="pr-2 space-y-2">
                                    {availableAppointments.map((apt, index) => (
                                      <div
                                        key={apt.id}
                                        onClick={() =>
                                          setSelectedAppointmentId(
                                            apt.id.toString(),
                                          )
                                        }
                                        className={cn(
                                          "p-2.5 rounded-md border cursor-pointer transition-all",
                                          selectedAppointmentId ===
                                            apt.id.toString()
                                            ? isDarkMode
                                              ? "border-gray-400 bg-gray-700"
                                              : "border-gray-900 bg-gray-100"
                                            : isDarkMode
                                              ? "border-gray-700 bg-gray-800 hover:border-gray-600"
                                              : "border-gray-300 bg-white hover:border-gray-400",
                                        )}
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center flex-1 gap-2">
                                            <div
                                              className={cn(
                                                "w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0",
                                                selectedAppointmentId ===
                                                  apt.id.toString()
                                                  ? "border-gray-900 bg-gray-900"
                                                  : isDarkMode
                                                    ? "border-gray-600"
                                                    : "border-gray-400",
                                              )}
                                            >
                                              {selectedAppointmentId ===
                                                apt.id.toString() && (
                                                <div className="w-1.5 h-1.5 rounded-full bg-white" />
                                              )}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2">
                                              {index === 0 && (
                                                <Badge className="text-xs text-white bg-gray-900 hover:bg-gray-900">
                                                  Next
                                                </Badge>
                                              )}
                                              <Badge
                                                variant="outline"
                                                className="text-xs"
                                              >
                                                {apt.date}
                                              </Badge>
                                              <Badge
                                                variant="outline"
                                                className="text-xs"
                                              >
                                                {apt.time}
                                              </Badge>
                                            </div>
                                          </div>
                                          <span
                                            className={cn(
                                              "text-xs font-medium",
                                              isDarkMode
                                                ? "text-gray-300"
                                                : "text-gray-700",
                                            )}
                                          >
                                            {apt.type}
                                          </span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </ScrollArea>
                              </CollapsibleContent>
                            </div>
                          </Collapsible>
                        )}
                    </div>
                  ) : (
                    <div
                      className={cn(
                        "p-3 rounded-lg border",
                        isDarkMode
                          ? "bg-gray-800/50 border-gray-700"
                          : "bg-gray-100 border-gray-300",
                      )}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <CalendarCheck
                          className={cn(
                            "w-4 h-4",
                            isDarkMode ? "text-gray-500" : "text-gray-400",
                          )}
                        />
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-500"
                          }
                        >
                          No upcoming appointments scheduled for this plan
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Ad-hoc Consultation Option */}
                  <div
                    className={cn(
                      "flex items-start space-x-3 p-3 rounded-lg border-2 transition-colors",
                      linkType === "adhoc"
                        ? isDarkMode
                          ? "border-gray-400 bg-gray-800"
                          : "border-gray-900 bg-gray-50"
                        : isDarkMode
                          ? "border-gray-700 hover:border-gray-600"
                          : "border-gray-200 hover:border-gray-300",
                      !canUseAdhoc &&
                        "opacity-50 cursor-not-allowed pointer-events-none",
                    )}
                    onClick={() => {
                      if (canUseAdhoc) {
                        setLinkType("adhoc");
                      }
                    }}
                  >
                    <RadioGroupItem
                      value="adhoc"
                      id="adhoc"
                      className="mt-0.5"
                      disabled={!canUseAdhoc}
                    />
                    <div className="flex-1 space-y-1">
                      <Label
                        htmlFor="adhoc"
                        className={cn(
                          "cursor-pointer font-medium flex items-center gap-2",
                          isDarkMode ? "text-gray-200" : "text-gray-900",
                        )}
                      >
                        <UserCheck
                          className={cn(
                            "w-4 h-4",
                            isDarkMode ? "text-gray-400" : "text-gray-500",
                          )}
                        />
                        Record as Ad-hoc Consultation
                      </Label>
                      <p
                        className={cn(
                          "text-sm pl-6",
                          isDarkMode ? "text-gray-400" : "text-gray-600",
                        )}
                      >
                        {fromConsultataion
                          ? "Link an ad-hoc appointment to this consultation (a scheduled followup will be used if available, or a new one created)"
                          : "Create an unscheduled consultation against this treatment plan"}
                      </p>
                    </div>
                  </div>
                </div>
              </RadioGroup>
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button
            variant="outline"
            onClick={handleSkip}
            disabled={isCreatingAdhocConsultation || isLinkingAppointment}
            className={
              isDarkMode
                ? "border-gray-600 text-gray-300 hover:bg-gray-800"
                : ""
            }
          >
            Skip - Continue Without Linking
          </Button>
          <Button
            onClick={
              linkType === "appointment"
                ? linktoSelectedAppointment
                : handleCreateAdhocConsultation
            }
            disabled={
              !selectedPlanId ||
              (linkType === "adhoc" && !canUseAdhoc) ||
              (linkType === "adhoc" && isCreatingAdhocConsultation) ||
              (linkType === "appointment" && isLinkingAppointment)
              // (linkType === "appointment" && fromConsultataion === false)
            }
            className={cn(
              "bg-black hover:bg-gray-800 text-white",
              (!selectedPlanId ||
                (linkType === "adhoc" && !canUseAdhoc) ||
                (linkType === "adhoc" && isCreatingAdhocConsultation) ||
                (linkType === "appointment" && isLinkingAppointment)) &&
                "opacity-50 cursor-not-allowed",
            )}
          >
            {isLinkingAppointment && linkType === "appointment" ? (
              <>
                <span className="inline-block w-3 h-3 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></span>
                Linking...
              </>
            ) : isCreatingAdhocConsultation && linkType === "adhoc" ? (
              <>
                <span className="inline-block w-3 h-3 mr-2 border-2 border-white rounded-full border-t-transparent animate-spin"></span>
                {fromConsultataion ? "Linking..." : "Creating..."}
              </>
            ) : linkType === "appointment" ? (
              "Link to Appointment"
            ) : fromConsultataion ? (
              "Create Ad-hoc Consultation"
            ) : (
              "Create Ad-hoc Consultation"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
